import type { WorkspacePersona } from "@/config/workspacePersona";

export type TeacherScenarioCategory =
  | "parent_family"
  | "student_behavior"
  | "colleague_staff"
  | "teacher_regulation"
  | "school_system";

export type TeacherScenarioMode = "prep" | "practice";

export type TeacherScenarioStage = "setup" | "plan" | "live" | "debrief";

export interface TeacherScenario {
  id: string;
  title: string;
  category: TeacherScenarioCategory;
  summary: string;
  openingPrompt: string;
  complications: string[];
  coachingGates: string[];
  debriefPrompts: string[];
  /** Minimum user turns in live stage before first complication. */
  complicationAfterTurns?: number;
  /** Hidden for support/classified personas (parent-facing scenarios). */
  parentFacing?: boolean;
  /** Optional override; defaults to category frameworks in teacherScenarioKnowledge.ts */
  relevantFrameworks?: string[];
  /** One line guiding principal takeaway tone */
  principalExportFocus?: string;
  /** True for user-authored scenarios saved to localStorage (My Scenarios). */
  isCustom?: boolean;
}

export const TEACHER_SCENARIO_CATEGORIES: {
  id: TeacherScenarioCategory;
  label: string;
  description: string;
}[] = [
  {
    id: "parent_family",
    label: "Parent & Family",
    description: "Navigate difficult family relationships and communication boundaries.",
  },
  {
    id: "student_behavior",
    label: "Student Behavior",
    description: "Practice classroom management and crisis response with coaching.",
  },
  {
    id: "colleague_staff",
    label: "Colleague & Staff",
    description: "Handle peer dynamics and supervisory conversations.",
  },
  {
    id: "teacher_regulation",
    label: "Teacher Regulation",
    description: "Build capacity to process stress, setbacks, and burnout.",
  },
  {
    id: "school_system",
    label: "School & System",
    description: "Navigate policy, compliance, and restorative processes.",
  },
];

export const TEACHER_SCENARIOS: TeacherScenario[] = [
  {
    id: "difficult-parent-talk",
    title: "Difficult Parent Conversation",
    category: "parent_family",
    summary: "A parent wants to discuss grades or behavior and emotions are running high.",
    openingPrompt: "A parent has requested a meeting about their child's recent grades. They seem frustrated before you've even started.",
    complications: [
      "Mid-conversation, the parent says the principal told them something different from what you shared.",
      "The parent pulls out their phone to record the conversation.",
    ],
    coachingGates: [
      "What is your opening line — and what are you trying to create in the first 30 seconds?",
      "What boundary will you hold if the conversation becomes accusatory?",
    ],
    debriefPrompts: ["What landed well?", "What would you do differently next time?"],
    parentFacing: true,
  },
  {
    id: "hostile-parent",
    title: "Hostile or Accusatory Parent",
    category: "parent_family",
    summary: "A parent arrives upset, blaming you for their child's struggles.",
    openingPrompt: "A parent emails that you are 'targeting' their child and demands an immediate meeting today.",
    complications: [
      "The parent raises their voice and says you don't care about their child.",
      "They threaten to go to the school board if you don't change the grade today.",
    ],
    coachingGates: [
      "Before you respond, what do you need to regulate in yourself?",
      "How will you respond without defending or escalating?",
    ],
    debriefPrompts: ["Where did you stay grounded?", "What language helped de-escalate — or what would have?"],
    parentFacing: true,
  },
  {
    id: "mental-health-concern",
    title: "Student Mental Health Concern",
    category: "parent_family",
    summary: "You need to communicate a mental health concern to a family with care and clarity.",
    openingPrompt: "You've noticed signs that a student may be struggling with anxiety or depression and need to talk with their parent.",
    complications: [
      "The parent says you're overreacting and that their child is 'just being a teenager.'",
      "The parent asks you not to involve the counselor because of stigma in their community.",
    ],
    coachingGates: [
      "How will you share observations without diagnosing?",
      "What resources or next steps can you offer while respecting family autonomy?",
    ],
    debriefPrompts: ["Did you balance care with clarity?", "What follow-up would you plan?"],
    parentFacing: true,
  },
  {
    id: "after-hours-contact",
    title: "Parent Contact Outside Hours",
    category: "parent_family",
    summary: "A parent is calling or texting at inappropriate times and expects immediate responses.",
    openingPrompt: "A parent has sent six texts since 9 p.m. about tomorrow's assignment and expects a reply tonight.",
    complications: [
      "The parent cc's your principal on an email saying you are unresponsive.",
      "The parent shows up at your classroom door before school starts, upset you didn't reply.",
    ],
    coachingGates: [
      "What boundary will you name — and how will you say it with warmth?",
      "How will you redirect to appropriate channels without damaging the relationship?",
    ],
    debriefPrompts: ["What boundary language felt authentic?", "How will you document this pattern?"],
    parentFacing: true,
  },
  {
    id: "student-outburst",
    title: "Student Outburst or Physical Incident",
    category: "student_behavior",
    summary: "A student throws an object or has a physical outburst during class.",
    openingPrompt: "During a transition, a student throws a chair across the room. The class is frozen. You are alone with 28 students.",
    complications: [
      "Another student starts recording on their phone while others are laughing.",
      "The student who threw the chair runs out of the room into the hallway.",
    ],
    coachingGates: [
      "Walk me through your first 60 seconds — safety, then connection.",
      "What will you say to the rest of the class while you manage the incident?",
    ],
    debriefPrompts: ["What prioritized safety without shame?", "What restorative follow-up would you plan?"],
    complicationAfterTurns: 2,
  },
  {
    id: "profanity-disrespect",
    title: "Profanity or Disrespect Toward Staff",
    category: "student_behavior",
    summary: "A student uses profanity or speaks disrespectfully to you or a colleague in front of others.",
    openingPrompt: "A student mutters a profanity at you when you redirect them, and several students heard it.",
    complications: [
      "The student denies it and says you are picking on them.",
      "A colleague insists you send the student to the office immediately.",
    ],
    coachingGates: [
      "What will you say in the moment — and what will you avoid?",
      "How will you address the behavior without labeling the student?",
    ],
    debriefPrompts: ["Did you separate behavior from identity?", "What would repair look like?"],
    complicationAfterTurns: 2,
  },
  {
    id: "chronic-behavior",
    title: "Chronic Behavior Patterns",
    category: "student_behavior",
    summary: "The same student has repeated referrals and you're meeting to plan a different approach.",
    openingPrompt: "You've written your fourth referral for the same student this month. You're meeting with your team to discuss next steps.",
    complications: [
      "A team member suggests the student just needs harsher consequences.",
      "The student's previous teacher says 'nothing works with this kid.'",
    ],
    coachingGates: [
      "What pattern are you seeing underneath the behavior?",
      "What is one shift you could make before escalating consequences?",
    ],
    debriefPrompts: ["What systemic factors did you name?", "What's your next experiment?"],
    complicationAfterTurns: 3,
  },
  {
    id: "emotional-crisis",
    title: "Student in Emotional Crisis",
    category: "student_behavior",
    summary: "A student is dysregulated, crying, or shutting down and won't engage.",
    openingPrompt: "A student put their head down and has been unresponsive for 20 minutes. When you approach, they start sobbing.",
    complications: [
      "The student says they don't want to live anymore — you need to respond carefully.",
      "Class is ending in five minutes and the student refuses to leave or go to the counselor.",
    ],
    coachingGates: [
      "What is your priority order: safety, connection, compliance?",
      "What will you say to stay present without trying to fix too fast?",
    ],
    debriefPrompts: ["When would you involve the counselor or admin?", "How did you regulate yourself?"],
    complicationAfterTurns: 2,
  },
  {
    id: "phone-conflict",
    title: "Cell Phone Confiscation Conflict",
    category: "student_behavior",
    summary: "A student refuses to give up their phone when you've asked per school policy.",
    openingPrompt: "You asked a student to place their phone in the classroom bin. They refuse and say you're violating their rights.",
    complications: [
      "The student calls their parent on speakerphone during class.",
      "The parent arrives at school demanding the phone back immediately.",
    ],
    coachingGates: [
      "How will you enforce policy without turning it into a power struggle?",
      "What options can you offer that preserve dignity?",
    ],
    debriefPrompts: ["Where did you avoid escalation?", "How would you debrief with the student later?"],
    complicationAfterTurns: 2,
  },
  {
    id: "co-teacher-conflict",
    title: "Conflict with Co-Teacher or Parapro",
    category: "colleague_staff",
    summary: "You and a co-teacher disagree on how to handle a student or classroom routine.",
    openingPrompt: "Your co-teacher corrected you in front of students during a lesson, and you felt undermined.",
    complications: [
      "The co-teacher tells you they've complained to the principal about your classroom management.",
      "Students have noticed the tension and ask why you two 'fight.'",
    ],
    coachingGates: [
      "What do you need them to understand about what happened for you?",
      "What is the shared goal you can anchor the conversation to?",
    ],
    debriefPrompts: ["What would a repair conversation sound like?", "What agreement could you make going forward?"],
    complicationAfterTurns: 3,
  },
  {
    id: "supervisor-feedback",
    title: "Critical Feedback from Supervisor",
    category: "colleague_staff",
    summary: "Your administrator gave critical feedback after an observation and you feel defensive.",
    openingPrompt: "After a walk-through, your principal said your pacing was off and students were disengaged. You disagree with their read.",
    complications: [
      "They ask you to submit a written improvement plan by Friday.",
      "A colleague mentions the principal talked about 'concerns' in a staff meeting.",
    ],
    coachingGates: [
      "What part of the feedback might contain a grain of truth?",
      "How will you respond professionally while advocating for your context?",
    ],
    debriefPrompts: ["What did you hear vs. what triggered you?", "What's one concrete change you could make?"],
    complicationAfterTurns: 3,
  },
  {
    id: "overwhelmed-before-day",
    title: "Overwhelmed Before a Hard Day",
    category: "teacher_regulation",
    summary: "You're dreading a difficult class, meeting, or conversation before the day starts.",
    openingPrompt: "It's 7 a.m. You have a hard conversation with a parent at 8, a challenging class period 2, and you're already exhausted.",
    complications: [
      "On the way in, a colleague vents about the same parent meeting and increases your anxiety.",
      "Your first class has a sub and the lesson plan wasn't followed — now you're behind.",
    ],
    coachingGates: [
      "What is actually in your control today?",
      "What is one regulation move you will use before the hard moment?",
    ],
    debriefPrompts: ["What helped you show up?", "What would you do differently tomorrow morning?"],
    complicationAfterTurns: 3,
  },
  {
    id: "lesson-went-badly",
    title: "Processing a Lesson That Went Badly",
    category: "teacher_regulation",
    summary: "A lesson flopped and you're spiraling about your competence.",
    openingPrompt: "Your lesson today fell apart — students were off-task, an activity failed, and you left feeling like a failure.",
    complications: [
      "A student said 'this class is boring' loud enough for others to hear.",
      "You have to teach the same content again tomorrow with no time to replan.",
    ],
    coachingGates: [
      "Separate the story you're telling yourself from what actually happened.",
      "What is one small repair or adjustment for tomorrow?",
    ],
    debriefPrompts: ["What evidence contradicts 'I'm a bad teacher'?", "Who could you debrief with?"],
    complicationAfterTurns: 2,
  },
  {
    id: "burnout-loss-of-spark",
    title: "Burnout and Loss of Spark",
    category: "teacher_regulation",
    summary: "You're going through the motions and wondering if you can keep doing this work.",
    openingPrompt: "You've noticed you're counting days until break, avoiding colleagues, and feeling numb about work you used to care about.",
    complications: [
      "A student you used to connect with says you seem 'different' and don't care anymore.",
      "You're asked to lead a new initiative when you barely have energy for your current load.",
    ],
    coachingGates: [
      "What would 'enough' look like this week — not forever, just this week?",
      "What is one thing that still matters to you in this work?",
    ],
    debriefPrompts: ["What boundary or support do you need?", "What's one micro-step toward renewal?"],
    complicationAfterTurns: 3,
  },
  {
    id: "iep-504-navigation",
    title: "Navigating IEP or 504 Situation",
    category: "school_system",
    summary: "A student's accommodations aren't being followed and you need to respond appropriately.",
    openingPrompt: "A student with a 504 plan isn't receiving their agreed accommodations, and the family is frustrated with your class.",
    complications: [
      "The case manager says they weren't informed about the missing accommodations.",
      "The parent requests a team meeting and cc's district special education.",
    ],
    coachingGates: [
      "What documentation do you need before responding?",
      "How will you partner with the family without over-promising?",
    ],
    debriefPrompts: ["What systemic issue did you identify?", "What's your follow-up with the team?"],
    complicationAfterTurns: 3,
  },
  {
    id: "restorative-after-incident",
    title: "Restorative Conversation After Incident",
    category: "school_system",
    summary: "After a classroom incident, you need to facilitate a restorative conversation.",
    openingPrompt: "Two days ago a student disrupted class and was removed. Today you're bringing them back for a restorative conversation.",
    complications: [
      "The student says the consequence was unfair and refuses to participate.",
      "Other students bring up the incident during the conversation and tensions rise again.",
    ],
    coachingGates: [
      "What does the student need to hear before they can take responsibility?",
      "How will you structure the conversation: harm, impact, repair?",
    ],
    debriefPrompts: ["Did you balance accountability with belonging?", "What repair agreement would you propose?"],
    complicationAfterTurns: 2,
  },
];

export function getTeacherScenarioById(id: string): TeacherScenario | undefined {
  return TEACHER_SCENARIOS.find((s) => s.id === id);
}

export function getTeacherScenariosForPersona(persona: WorkspacePersona): TeacherScenario[] {
  const hideParentFacing =
    persona === "support_staff" || persona === "classified_staff";
  return TEACHER_SCENARIOS.filter((s) => !hideParentFacing || !s.parentFacing);
}

export const TEACHER_SCENARIO_STAGES: { id: TeacherScenarioStage; label: string; micro: string }[] = [
  { id: "setup", label: "Setup", micro: "Choose scenario & mode" },
  { id: "plan", label: "Your Plan", micro: "Describe your approach" },
  { id: "live", label: "Live Scenario", micro: "Practice the moment" },
  { id: "debrief", label: "Debrief & Grow", micro: "Reflect, share & save" },
];
